import { useState, useEffect, useMemo } from "react";
import { MedicalKnowledgeService, MedicalKnowledgeEntry } from "@/services/medicalKnowledge";
import { ICD11Service, ICD11Entry } from "@/services/icd11Service";
import { generateFastDiagnosticAnalysis } from "@/utils/fastDiagnosticAnalysis";
import { AdvancedDiagnosticAnalysisService } from "@/services/advancedDiagnosticAnalysis";
import { extractSymptomsFromAnswers, extractConditionsFromAnswers, calculateEvidenceBasedAmIOKScore } from "@/utils/resultsUtils";

interface EnhancedMedicalData {
  relevantResearch: MedicalKnowledgeEntry[];
  clinicalGuidelines: MedicalKnowledgeEntry[];
  drugSafetyInfo: MedicalKnowledgeEntry[];
  evidenceLevel: string;
  confidence: number;
  evidenceBasedScore?: {
    score: number;
    confidence: number;
    reasoning: string[];
  };
  icd11Data?: {
    primaryConditions: ICD11Entry[];
    secondaryConditions: ICD11Entry[];
    symptomMappings: Array<{ symptom: string; icdEntries: ICD11Entry[] }>;
  };
}

interface DiagnosticAnalysis {
  potentialDiagnoses: any[];
  differentialDiagnosis: string[];
  clinicalReasoningPath: string[];
  evidenceLevel: string;
  confidence: number;
  scientificBasis: string[];
}

// Fallback functions for when database doesn't return comprehensive results
const generateFallbackDiagnoses = (symptoms: string[], answers: Record<string, any>) => {
  const category = answers.symptom_constellation?.category || 'General Assessment';
  const severity = parseInt(answers.daily_impact) || 5;
  
  return [{
    condition: `${category} - Further Evaluation Needed`,
    probability: 75,
    severity: severity > 7 ? 'moderate' : 'mild',
    reasoning: `Symptoms in ${category} category require clinical assessment for accurate diagnosis`,
    clinicalPearls: [`${category} symptoms benefit from systematic evaluation`],
    requiredTests: ['Clinical assessment', 'Basic investigations as indicated']
  }];
};

const generateFallbackReasoning = (symptoms: string[], answers: Record<string, any>) => {
  const category = answers.symptom_constellation?.category || 'General';
  const severity = parseInt(answers.daily_impact) || 5;
  
  return [
    `Patient presents with symptoms in ${category} category`,
    `Daily impact rated ${severity}/10 indicating ${severity >= 7 ? 'significant' : 'moderate'} functional impairment`,
    'Systematic clinical evaluation recommended to establish diagnosis',
    'Evidence-based assessment protocols should guide further investigation'
  ];
};

// Generate intelligent recommendations based on ICD-11 and medical data
const generateIntelligentRecommendations = (
  enhancedAnalysis: any,
  icd11Analysis: any,
  responses: { field: string; symptoms: string[]; duration: string; pattern: string; impact: number }
): string[] => {
  const recommendations = [];

  // ICD-11 based recommendations
  if (icd11Analysis?.primaryConditions?.length > 0) {
    const highSeverityConditions = icd11Analysis.primaryConditions.filter(
      (c: ICD11Entry) => c.severity_level === 'high'
    );
    
    if (highSeverityConditions.length > 0) {
      recommendations.push(
        'Your symptoms match serious conditions in WHO ICD-11 - seek immediate medical evaluation',
        'Consider emergency care if symptoms are severe or worsening rapidly'
      );
    } else {
      recommendations.push(
        'Your symptoms match known medical conditions - schedule an appointment with your healthcare provider',
        'Keep a symptom diary to track changes and patterns'
      );
    }
  }

  // Duration-based recommendations
  if (responses.duration.includes('chronic') || responses.duration.includes('months')) {
    recommendations.push('Long-term symptoms warrant comprehensive evaluation by a specialist');
  } else if (responses.duration.includes('acute') || responses.duration.includes('hours')) {
    recommendations.push('Sudden onset symptoms require prompt medical attention');
  }

  // Impact-based recommendations
  if (responses.impact >= 8) {
    recommendations.push(
      'High impact on daily life indicates need for urgent medical assessment',
      'Consider seeking care today or tomorrow'
    );
  } else if (responses.impact >= 6) {
    recommendations.push('Moderate impact suggests scheduling medical evaluation within a week');
  }

  // Body system specific recommendations
  const bodySystemAdvice: Record<string, string> = {
    'chest/breathing': 'Respiratory symptoms can indicate serious conditions - consider urgent evaluation',
    'mental health': 'Mental health symptoms benefit from professional support and treatment',
    'muscle/bones': 'Musculoskeletal issues may require imaging or physical therapy evaluation',
    'stomach/digestion': 'Digestive symptoms may indicate underlying conditions requiring medical assessment'
  };

  if (bodySystemAdvice[responses.field]) {
    recommendations.push(bodySystemAdvice[responses.field]);
  }

  // Add default recommendations if none generated
  if (recommendations.length === 0) {
    recommendations.push(
      'Consider consulting with a healthcare provider for proper evaluation',
      'Document your symptoms and their progression',
      'Monitor symptom changes and seek care if symptoms worsen'
    );
  }

  // Remove duplicates and limit to 5 most relevant
  return [...new Set(recommendations)].slice(0, 5);
};

export const useResultsData = (answers: Record<string, any>) => {
  const [enhancedData, setEnhancedData] = useState<EnhancedMedicalData | null>(null);
  const [diagnosticAnalysis, setDiagnosticAnalysis] = useState<DiagnosticAnalysis | null>(null);
  const [isLoadingKnowledge, setIsLoadingKnowledge] = useState(true);

  // Memoize extracted symptoms and conditions to prevent infinite loops
  const extractedSymptoms = useMemo(() => extractSymptomsFromAnswers(answers), [answers]);
  const extractedConditions = useMemo(() => extractConditionsFromAnswers(answers), [answers]);

  useEffect(() => {
    const loadEnhancedMedicalData = async () => {
      if (extractedSymptoms.length === 0) {
        setIsLoadingKnowledge(false);
        return;
      }

      try {
        console.log('Loading advanced medical database analysis for symptoms:', extractedSymptoms);
        
        // Use the new advanced diagnostic analysis service
        const [advancedAnalysis, icd11Analysis] = await Promise.all([
          AdvancedDiagnosticAnalysisService.generateIntelligentDiagnosticAnalysis(answers),
          ICD11Service.mapResponsesToICD11({
            field: answers?.q2_field?.answer || '',
            symptoms: extractedSymptoms,
            duration: answers?.q4_duration?.answer || '',
            pattern: answers?.q5_pattern?.answer || '',
            impact: answers?.q6_impact?.answer || 5
          })
        ]);
        
        console.log('Advanced diagnostic analysis result:', advancedAnalysis);
        console.log('ICD-11 analysis result:', icd11Analysis);

        // Convert advanced analysis to expected format
        setDiagnosticAnalysis({
          potentialDiagnoses: advancedAnalysis.potentialDiagnoses.map(d => ({
            condition: d.condition,
            probability: d.probability,
            reasoning: d.reasoning,
            clinicalPearls: d.clinicalPearls || [],
            severity: d.severity,
            requiredTests: d.requiredTests || [],
            laymanDescription: d.laymanDescription
          })),
          differentialDiagnosis: advancedAnalysis.potentialDiagnoses.map(d => d.condition),
          clinicalReasoningPath: advancedAnalysis.clinicalReasoningPath,
          evidenceLevel: advancedAnalysis.evidenceQuality,
          confidence: advancedAnalysis.databaseCoverage.icd11Matches > 5 ? 0.9 : 0.8,
          scientificBasis: advancedAnalysis.potentialDiagnoses.slice(0, 5).flatMap(d => d.supportingStudies)
        });

        // Get enhanced medical knowledge data with more comprehensive search
        const relevantResearch = extractedSymptoms.length > 0 
          ? await MedicalKnowledgeService.searchRelevantKnowledge(extractedSymptoms, [], ['clinical_research'], 15)
          : [];

        // Calculate evidence-based AmIOK score using all available data
        const evidenceBasedScore = await calculateEvidenceBasedAmIOKScore(
          answers,
          relevantResearch,
          advancedAnalysis
        );

        console.log('Evidence-based scoring result:', evidenceBasedScore);

        setEnhancedData({
          relevantResearch,
          clinicalGuidelines: advancedAnalysis.potentialDiagnoses.slice(0, 5).map(d => ({ 
            title: `Clinical guideline for ${d.condition}`, 
            id: '', 
            source: 'clinical_guidelines' 
          })) as MedicalKnowledgeEntry[],
          drugSafetyInfo: [],
          evidenceLevel: advancedAnalysis.evidenceQuality,
          confidence: evidenceBasedScore.confidence,
          evidenceBasedScore,
          icd11Data: icd11Analysis
        });

        console.log('Advanced diagnostic analysis completed:', {
          totalConditionsConsidered: advancedAnalysis.totalConditionsConsidered,
          potentialDiagnoses: advancedAnalysis.potentialDiagnoses.length,
          icd11PrimaryConditions: icd11Analysis.primaryConditions.length,
          icd11SecondaryConditions: icd11Analysis.secondaryConditions.length,
          confidenceLevel: Math.round(evidenceBasedScore.confidence * 100) + '%',
          evidenceQuality: advancedAnalysis.evidenceQuality,
          evidenceBasedScore: evidenceBasedScore.score,
          databaseCoverage: advancedAnalysis.databaseCoverage,
          scoringReasoning: evidenceBasedScore.reasoning
        });

      } catch (error) {
        console.error('Error loading optimized analysis:', error);
        // Fallback with no external data
        setEnhancedData({
          relevantResearch: [],
          clinicalGuidelines: [],
          drugSafetyInfo: [],
          evidenceLevel: 'medium',
          confidence: 0.8,
          icd11Data: {
            primaryConditions: [],
            secondaryConditions: [],
            symptomMappings: []
          }
        });
      } finally {
        setIsLoadingKnowledge(false);
      }
    };

    loadEnhancedMedicalData();
  }, [extractedSymptoms, answers]);

  const getTotalEvidence = () => {
    if (!enhancedData) return 0;
    return enhancedData.relevantResearch.length + 
           enhancedData.clinicalGuidelines.length + 
           enhancedData.drugSafetyInfo.length +
           (enhancedData.icd11Data?.primaryConditions.length || 0) +
           (enhancedData.icd11Data?.secondaryConditions.length || 0);
  };

  return {
    enhancedData,
    diagnosticAnalysis,
    isLoadingKnowledge,
    getTotalEvidence
  };
};